'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import EmailCaptureForm from '@/components/ui/EmailCaptureForm';
import { Arc, Stamp } from '@/components/instrument';
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  TrendingUp,
  Target,
  Calendar,
  Zap,
  Building2,
  Rocket,
  Users,
  Globe,
  Search,
  Share2,
} from 'lucide-react';

const steps = [
  {
    id: 'step-stage',
    question: 'What stage is your business at?',
    subtext: 'This helps us tailor recommendations to where you are right now.',
    field: 'stage',
    icon: Building2,
    options: [
      {
        id: 'opt-idea',
        value: 'idea',
        label: 'Idea / Pre-launch',
        desc: "I have a business concept but haven't launched yet",
      },
      {
        id: 'opt-early',
        value: 'early',
        label: 'Early stage (0–12 months)',
        desc: 'Recently launched, building initial traction',
      },
      {
        id: 'opt-growth',
        value: 'growth',
        label: 'Growth stage (1–3 years)',
        desc: 'Established with some revenue, looking to scale',
      },
      {
        id: 'opt-scale',
        value: 'scale',
        label: 'Scaling (3+ years)',
        desc: 'Proven business model, expanding aggressively',
      },
    ],
  },
  {
    id: 'step-industry',
    question: 'Which industry best describes your business?',
    subtext: 'We have deep experience in specific sectors and tailor our approach accordingly.',
    field: 'industry',
    icon: Globe,
    options: [
      {
        id: 'opt-hospitality',
        value: 'hospitality',
        label: 'Hospitality',
        desc: 'Hotels, restaurants, cafés, Airbnb',
      },
      {
        id: 'opt-ecommerce',
        value: 'ecommerce',
        label: 'E-commerce / Online Retail',
        desc: 'Shops, digital products, online stores',
      },
      {
        id: 'opt-services',
        value: 'services',
        label: 'Professional Services',
        desc: 'Consultants, agencies, professionals, solopreneurs',
      },
      {
        id: 'opt-manufacturing',
        value: 'manufacturing',
        label: 'Manufacturing / Distribution',
        desc: 'Producers, suppliers, distributors',
      },
      { id: 'opt-other', value: 'other', label: 'Other', desc: 'My industry is not listed here' },
    ],
  },
  {
    id: 'step-goal',
    question: 'What is your primary growth goal for the next 12 months?',
    subtext: 'Select the outcome that matters most to you right now.',
    field: 'goal',
    icon: Target,
    options: [
      {
        id: 'opt-awareness',
        value: 'awareness',
        label: 'Build brand awareness',
        desc: 'More people knowing who we are and what we do',
      },
      {
        id: 'opt-leads',
        value: 'leads',
        label: 'Generate more leads',
        desc: 'Consistent flow of qualified enquiries and prospects',
      },
      {
        id: 'opt-revenue',
        value: 'revenue',
        label: 'Increase revenue',
        desc: 'Convert more leads and grow monthly recurring revenue',
      },
      {
        id: 'opt-retention',
        value: 'retention',
        label: 'Improve customer retention',
        desc: 'Keep existing customers longer and increase LTV',
      },
    ],
  },
  {
    id: 'step-marketing',
    question: 'How would you describe your current marketing maturity?',
    subtext: 'Be honest — this helps us give you the most relevant recommendations.',
    field: 'marketingMaturity',
    icon: TrendingUp,
    options: [
      {
        id: 'opt-none',
        value: 'none',
        label: 'No formal marketing',
        desc: 'We rely entirely on word of mouth and referrals',
      },
      {
        id: 'opt-basic',
        value: 'basic',
        label: 'Basic presence',
        desc: 'We have social media and a website but no active strategy',
      },
      {
        id: 'opt-active',
        value: 'active',
        label: 'Active but inconsistent',
        desc: 'We run campaigns sometimes but results are unpredictable',
      },
      {
        id: 'opt-systematic',
        value: 'systematic',
        label: 'Systematic approach',
        desc: 'We have defined funnels, KPIs, and regular reporting',
      },
    ],
  },
  {
    id: 'step-challenge',
    question: 'What is your single biggest business challenge right now?',
    subtext: 'Select the challenge that is most urgent for your business.',
    field: 'challenge',
    icon: Zap,
    options: [
      {
        id: 'opt-strategy',
        value: 'strategy',
        label: 'No clear growth strategy',
        desc: "I know I need to grow but don't have a roadmap",
      },
      {
        id: 'opt-positioning',
        value: 'positioning',
        label: 'Weak market positioning',
        desc: "Customers don't clearly understand why to choose us",
      },
      {
        id: 'opt-leads-chal',
        value: 'inconsistent_leads',
        label: 'Inconsistent lead flow',
        desc: 'New customers come in bursts with no reliable system',
      },
      {
        id: 'opt-conversion',
        value: 'low_conversion',
        label: 'Low conversion rates',
        desc: 'Traffic comes in but not enough becomes paying customers',
      },
      {
        id: 'opt-brand',
        value: 'brand',
        label: 'Brand differentiation',
        desc: 'We look and sound like every other competitor',
      },
      {
        id: 'opt-operations',
        value: 'operations',
        label: 'Operational scaling',
        desc: 'Growth is happening but processes are breaking',
      },
    ],
  },
  {
    id: 'step-team',
    question: 'What is the size of your team?',
    subtext: 'This helps us recommend the right level of support.',
    field: 'teamSize',
    icon: Users,
    options: [
      { id: 'opt-solo', value: 'solo', label: 'Solo founder', desc: "It's just me right now" },
      { id: 'opt-small', value: 'small', label: '2–5 people', desc: 'Small but mighty core team' },
      {
        id: 'opt-medium',
        value: 'medium',
        label: '6–20 people',
        desc: 'Growing team with some structure',
      },
      {
        id: 'opt-large',
        value: 'large',
        label: '20+ people',
        desc: 'Established company with departments',
      },
    ],
  },
  {
    id: 'step-channels',
    question: 'Which customer acquisition channels are you currently using?',
    subtext: 'Select all that apply.',
    field: 'channels',
    multiple: true,
    icon: Share2,
    options: [
      {
        id: 'opt-organic',
        value: 'organic_social',
        label: 'Organic social media',
        desc: 'Regular posting on Instagram, LinkedIn, etc.',
      },
      {
        id: 'opt-paid-social',
        value: 'paid_social',
        label: 'Paid social ads',
        desc: 'Facebook, Instagram, TikTok ads',
      },
      {
        id: 'opt-google',
        value: 'google_ads',
        label: 'Google Ads',
        desc: 'Search and display advertising',
      },
      { id: 'opt-seo', value: 'seo', label: 'SEO / Content', desc: 'Organic search traffic' },
      {
        id: 'opt-referral',
        value: 'referral',
        label: 'Referrals / Word of mouth',
        desc: 'Customer and partner referrals',
      },
      {
        id: 'opt-email',
        value: 'email',
        label: 'Email marketing',
        desc: 'Newsletters, drip campaigns',
      },
    ],
  },
  {
    id: 'step-timeline',
    question: 'When are you looking to start?',
    subtext: 'This helps us prioritise and plan your onboarding.',
    field: 'timeline',
    icon: Rocket,
    options: [
      {
        id: 'opt-now',
        value: 'immediately',
        label: 'Immediately',
        desc: "I'm ready to get started as soon as possible",
      },
      {
        id: 'opt-month',
        value: 'within_month',
        label: 'Within the next month',
        desc: "I'm planning and want to be ready soon",
      },
      {
        id: 'opt-quarter',
        value: 'next_quarter',
        label: 'Next quarter',
        desc: "I'm evaluating options for Q3/Q4",
      },
      {
        id: 'opt-exploring',
        value: 'exploring',
        label: 'Just exploring',
        desc: "I'm learning what's available before deciding",
      },
    ],
  },
];

const serviceMap: Record<string, string[]> = {
  strategy: ['Business Consulting', 'Paid Ads Management'],
  positioning: ['Digital Design', 'Business Consulting'],
  inconsistent_leads: ['Paid Ads Management', 'Social Media Marketing'],
  low_conversion: ['SEO & Content Creation', 'Website Creation'],
  brand: ['Digital Design', 'Social Media Marketing'],
  operations: ['Business Consulting'],
  awareness: ['Social Media Marketing', 'Digital Design'],
  leads: ['Paid Ads Management', 'SEO & Content Creation'],
  revenue: ['Business Consulting', 'Paid Ads Management'],
  retention: ['Social Media Marketing', 'Business Consulting'],
};

function calculateScore(answers: Record<string, string | string[]>): number {
  let score = 40;
  if (answers.stage === 'growth') score += 15;
  if (answers.stage === 'scale') score += 20;
  if (answers.stage === 'early') score += 8;
  if (answers.marketingMaturity === 'systematic') score += 25;
  if (answers.marketingMaturity === 'active') score += 15;
  if (answers.marketingMaturity === 'basic') score += 8;
  if (answers.timeline === 'immediately') score += 10;
  if (answers.timeline === 'within_month') score += 5;
  if (Array.isArray(answers.channels) && answers.channels.length > 2) score += 10;
  return Math.min(score, 99);
}

function getRecommendations(answers: Record<string, string | string[]>): string[] {
  const challenge = answers.challenge as string;
  const goal = answers.goal as string;
  const recs = new Set<string>();
  if (challenge && serviceMap[challenge]) serviceMap[challenge].forEach((s) => recs.add(s));
  if (goal && serviceMap[goal]) serviceMap[goal].forEach((s) => recs.add(s));
  if (recs.size < 2) recs.add('Business Consulting');
  return Array.from(recs).slice(0, 3);
}

function getPriorities(answers: Record<string, string | string[]>): string[] {
  const priorities: string[] = [];
  if (answers.marketingMaturity === 'none' || answers.marketingMaturity === 'basic') {
    priorities.push('Establish a consistent marketing presence before running paid campaigns');
  }
  if (answers.challenge === 'strategy' || answers.challenge === 'positioning') {
    priorities.push('Clarify your positioning and target customer before investing in acquisition');
  }
  if (answers.challenge === 'inconsistent_leads' || answers.goal === 'leads') {
    priorities.push('Build a repeatable lead generation system with clear KPIs');
  }
  if (priorities.length < 3) {
    priorities.push('Define your 90-day growth goals with measurable outcomes');
    priorities.push('Audit your current marketing spend and identify the highest-ROI channels');
  }
  return priorities.slice(0, 3);
}

const stepVariants = {
  enter: (dir: number) => ({ opacity: 0, x: dir > 0 ? 24 : -24 }),
  center: { opacity: 1, x: 0 },
  exit: (dir: number) => ({ opacity: 0, x: dir > 0 ? -24 : 24 }),
};

export default function AssessmentFlow() {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [direction, setDirection] = useState(1);
  const [completed, setCompleted] = useState(false);
  // Sits between the last question and the report. Answering seven questions
  // and vanishing is the single biggest leak on the site — this is where a
  // finished assessment becomes a lead Noventi Labs can actually follow up.
  const [askingEmail, setAskingEmail] = useState(false);

  const step = steps[currentStep];
  const StepIcon = step.icon;
  const progress = (currentStep / steps.length) * 100;
  const isLastStep = currentStep === steps.length - 1;

  const handleSelect = (value: string) => {
    if (step.multiple) {
      const current = (answers[step.field] as string[]) || [];
      const updated = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      setAnswers((prev) => ({ ...prev, [step.field]: updated }));
    } else {
      setAnswers((prev) => ({ ...prev, [step.field]: value }));
    }
  };

  const isSelected = (value: string) => {
    if (step.multiple) {
      return ((answers[step.field] as string[]) || []).includes(value);
    }
    return answers[step.field] === value;
  };

  const canAdvance = step.multiple
    ? ((answers[step.field] as string[]) || []).length > 0
    : !!answers[step.field];

  const handleNext = () => {
    if (!canAdvance) return;
    if (isLastStep) {
      setAskingEmail(true);
    } else {
      setDirection(1);
      setCurrentStep((s) => s + 1);
    }
  };

  const handleBack = () => {
    if (currentStep === 0) return;
    setDirection(-1);
    setCurrentStep((s) => s - 1);
  };

  if (askingEmail && !completed) {
    const score = calculateScore(answers);
    return (
      <div className="min-h-screen m-ground">
        <div
          style={{
            background: 'var(--m-band)',
            borderBottom: '1px solid var(--m-band-edge)',
            paddingTop: 80,
            paddingBottom: 48,
          }}
        >
          <div className="max-w-3xl mx-auto px-6">
            <p
              className="font-mono text-[10px] uppercase tracking-widest mb-3"
              style={{ color: 'rgba(var(--m-on-band-rgb),0.4)' }}
            >
              Assessment complete — one last step
            </p>
            <h1
              className="font-bold uppercase"
              style={{
                fontFamily: "'Archivo Narrow', sans-serif",
                fontSize: 'clamp(26px, 5vw, 44px)',
                color: 'var(--m-on-band)',
                letterSpacing: '0.02em',
                lineHeight: 1.1,
              }}
            >
              Where should we send your report?
            </h1>
            <p
              className="mt-4"
              style={{ color: 'rgba(var(--m-on-band-rgb),0.55)', fontSize: 15, lineHeight: 1.65 }}
            >
              Your readiness score is {score}/100. Leave your email and we will send the full
              breakdown, or skip straight to it on screen.
            </p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 py-10">
          <EmailCaptureForm
            source="assessment"
            context="business-assessment"
            details={{ ...answers, score }}
            buttonLabel="Send my report"
            placeholder="you@company.com"
            successMessage="Saved. Opening your report…"
            onSuccess={() => setCompleted(true)}
          />
          <p className="mt-3" style={{ fontSize: 13, color: 'var(--m-ink-6)' }}>
            No spam, and no sharing your address. Unsubscribe whenever you like.
          </p>

          {/* Skipping is allowed on purpose. Trapping the report behind the
              form would raise capture a little and cost trust a lot. */}
          <button
            type="button"
            onClick={() => setCompleted(true)}
            className="font-mono uppercase tracking-widest mt-6 min-h-[44px] inline-flex items-center"
            style={{
              fontSize: 11,
              letterSpacing: '0.12em',
              color: 'var(--m-ink-6)',
              background: 'transparent',
              border: 'none',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            Skip — just show me the results
          </button>
        </div>
      </div>
    );
  }

  if (completed) {
    const score = calculateScore(answers);
    const recommendations = getRecommendations(answers);
    const priorities = getPriorities(answers);
    const scoreLabel =
      score >= 70 ? 'High Readiness' : score >= 50 ? 'Moderate Readiness' : 'Early Stage';

    return (
      <div className="min-h-screen m-ground">
        {/* Dark header */}
        <div
          style={{
            background: 'var(--m-band)',
            borderBottom: '1px solid var(--m-band-edge)',
            paddingTop: 80,
            paddingBottom: 48,
          }}
        >
          <div className="max-w-3xl mx-auto px-6">
            <p
              className="font-mono text-[10px] uppercase tracking-widest mb-3"
              style={{ color: 'rgba(var(--m-on-band-rgb),0.4)' }}
            >
              Assessment Complete — Diagnostic Report
            </p>
            <h1
              className="font-bold uppercase"
              style={{
                fontFamily: "'Archivo Narrow', sans-serif",
                fontSize: 'clamp(22px, 3.5vw, 36px)',
                color: 'var(--m-on-band)',
                letterSpacing: '0.03em',
                lineHeight: 1.1,
              }}
            >
              Your Business Diagnostic
            </h1>
            <p
              style={{
                fontSize: 13,
                color: 'rgba(var(--m-on-band-rgb),0.45)',
                fontFamily: "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                marginTop: 10,
                maxWidth: 480,
              }}
            >
              Based on your responses, here is your personalised business diagnosis. These are
              initial recommendations — not guaranteed outcomes.
            </p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 py-12">
          {/* Score */}
          {/* The reading, plotted. This is the one moment on the site where the
              hero ring's instrument language is literally true — something was
              measured — so the score is drawn on a dial rather than printed as
              a number in a box. Static SVG: no canvas, no animation loop. */}
          <div
            className="mb-8 p-8 flex flex-col items-center text-center"
            style={{
              border: '1px solid rgba(var(--m-ink-rgb),0.1)',
              background: 'var(--m-surface-2)',
            }}
          >
            <Stamp
              align="center"
              className="mb-6"
              lines={['Consultation readiness', `${steps.length} questions answered`]}
            />
            <Arc
              value={score / 100}
              size={236}
              ticks={6}
              animate
              label={`Consultation readiness score: ${score} out of 100, ${scoreLabel}`}
              className="mb-5"
            >
              <span
                className="font-bold"
                style={{
                  fontSize: 60,
                  color: 'var(--m-ink)',
                  fontVariantNumeric: 'tabular-nums',
                  fontFamily: "'Archivo Narrow', sans-serif",
                  lineHeight: 1,
                }}
              >
                {score}
              </span>
              <span
                className="font-mono uppercase"
                style={{
                  fontSize: 10,
                  letterSpacing: '0.16em',
                  color: 'var(--m-ink-6)',
                  marginTop: 6,
                }}
              >
                {scoreLabel}
              </span>
            </Arc>
            <p
              style={{
                fontSize: 13,
                color: 'var(--m-ink-5)',
                fontFamily: "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                lineHeight: 1.65,
                maxWidth: 420,
                margin: '0 auto',
              }}
            >
              {score >= 70
                ? 'You have a solid foundation. The right strategy and execution support could accelerate your growth significantly.'
                : score >= 50
                  ? 'You are on the right track but have clear gaps to address before scaling your marketing investment.'
                  : 'Focus on fundamentals first — a strong strategy and positioning foundation will make every future investment more effective.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Recommended services */}
            <div style={{ border: '1px solid rgba(var(--m-ink-rgb),0.1)', padding: 20 }}>
              <p
                className="font-mono text-[10px] uppercase tracking-widest mb-4"
                style={{ color: 'var(--m-muted)' }}
              >
                Recommended Services
              </p>
              <div className="space-y-2">
                {recommendations.map((rec, i) => (
                  <div
                    key={`rec-${i}`}
                    className="flex items-center gap-2.5 py-2.5"
                    style={{
                      borderBottom:
                        i < recommendations.length - 1
                          ? '1px solid rgba(var(--m-ink-rgb),0.07)'
                          : 'none',
                    }}
                  >
                    <CheckCircle2
                      className="w-3.5 h-3.5 flex-shrink-0"
                      style={{ color: 'var(--m-ink)' }}
                    />
                    <span
                      style={{
                        fontSize: 13,
                        color: 'var(--m-ink-3)',
                        fontFamily:
                          "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                      }}
                    >
                      {rec}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Priorities */}
            <div style={{ border: '1px solid rgba(var(--m-ink-rgb),0.1)', padding: 20 }}>
              <p
                className="font-mono text-[10px] uppercase tracking-widest mb-4"
                style={{ color: 'var(--m-muted)' }}
              >
                Suggested Priorities
              </p>
              <div className="space-y-3">
                {priorities.map((p, i) => (
                  <div key={`pri-${i}`} className="flex items-start gap-2.5">
                    <span
                      className="font-mono flex-shrink-0"
                      style={{ fontSize: 9, color: 'var(--m-muted-3)', marginTop: 3, minWidth: 16 }}
                    >
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <p
                      style={{
                        fontSize: 12,
                        color: 'var(--m-ink-5)',
                        fontFamily:
                          "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                        lineHeight: 1.55,
                      }}
                    >
                      {p}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Summary */}
          <div
            style={{
              border: '1px solid rgba(var(--m-ink-rgb),0.1)',
              padding: 20,
              marginBottom: 16,
            }}
          >
            <p
              className="font-mono text-[10px] uppercase tracking-widest mb-4"
              style={{ color: 'var(--m-muted)' }}
            >
              Your Assessment Summary
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Business Stage', value: answers.stage as string },
                { label: 'Industry', value: answers.industry as string },
                { label: 'Primary Goal', value: answers.goal as string },
                { label: 'Timeline', value: answers.timeline as string },
              ].map((item) => (
                <div
                  key={`summary-${item.label}`}
                  style={{
                    borderBottom: '1px solid rgba(var(--m-ink-rgb),0.07)',
                    paddingBottom: 10,
                  }}
                >
                  <p
                    className="font-mono text-[9px] uppercase tracking-widest mb-1"
                    style={{ color: 'var(--m-muted-3)' }}
                  >
                    {item.label}
                  </p>
                  <p
                    style={{
                      fontSize: 12,
                      color: 'var(--m-ink)',
                      fontFamily: "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                      fontWeight: 600,
                      textTransform: 'capitalize',
                    }}
                  >
                    {item.value?.replace(/_/g, ' ') || '—'}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <p
            style={{
              fontSize: 11,
              color: 'var(--m-muted-3)',
              fontFamily: "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
              lineHeight: 1.6,
              marginBottom: 24,
              padding: '10px 14px',
              background: 'rgba(var(--m-ink-rgb),0.02)',
              border: '1px solid rgba(var(--m-ink-rgb),0.06)',
            }}
          >
            These results are an initial recommendation based on your responses and are intended as
            a starting point for conversation — not a guaranteed business outcome.
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              href="/book-a-consultation"
              className="flex-1 inline-flex items-center justify-center gap-2 font-mono uppercase text-[10px] tracking-widest py-3 transition-opacity hover:opacity-80"
              style={{ background: 'var(--m-ink)', color: 'var(--m-bg)', textDecoration: 'none' }}
            >
              <Calendar className="w-3.5 h-3.5" />[ Book a Consultation ]
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen m-ground">
      {/* Dark header */}
      <div
        style={{
          background: 'var(--m-band)',
          borderBottom: '1px solid var(--m-band-edge)',
          paddingTop: 80,
          paddingBottom: 40,
        }}
      >
        <div className="max-w-2xl mx-auto px-6">
          <p
            className="font-mono text-[10px] uppercase tracking-widest mb-3"
            style={{ color: 'rgba(var(--m-on-band-rgb),0.4)' }}
          >
            Business Assessment
          </p>
          <h1
            className="font-bold uppercase"
            style={{
              fontFamily: "'Archivo Narrow', sans-serif",
              fontSize: 'clamp(20px, 3vw, 32px)',
              color: 'var(--m-on-band)',
              letterSpacing: '0.03em',
              lineHeight: 1.1,
            }}
          >
            Diagnose Your Business
          </h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-10">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <StepIcon className="w-3.5 h-3.5" style={{ color: 'var(--m-muted)' }} />
              <span
                className="font-mono text-[10px] uppercase tracking-widest"
                style={{ color: 'var(--m-muted)' }}
              >
                Step {currentStep + 1} of {steps.length}
              </span>
            </div>
            <span
              className="font-mono text-[10px] uppercase tracking-widest"
              /* muted-4 sat at 1.9:1 against the page; the progress figure is
                 information, not decoration, so it needs to be readable. */
              style={{ color: 'var(--m-ink-6)' }}
            >
              {Math.round(progress)}% complete
            </span>
          </div>

          {/* Progress bar */}
          <div
            style={{ height: 1, background: 'rgba(var(--m-ink-rgb),0.08)', position: 'relative' }}
          >
            <motion.div
              style={{
                height: '100%',
                background: 'var(--m-ink)',
                position: 'absolute',
                top: 0,
                left: 0,
              }}
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            />
          </div>

          {/* Step dots */}
          <div className="flex gap-1 mt-2">
            {steps.map((_, i) => (
              <div
                key={`dot-${i}`}
                className="h-0.5 flex-1 rounded-full transition-all duration-300"
                style={{
                  background:
                    i < currentStep
                      ? 'var(--m-ink)'
                      : i === currentStep
                        ? 'rgba(var(--m-ink-rgb),0.35)'
                        : 'rgba(var(--m-ink-rgb),0.08)',
                }}
              />
            ))}
          </div>
        </div>

        {/* Step content */}
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={`assessment-step-${currentStep}`}
            custom={direction}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.22, ease: 'easeInOut' }}
          >
            <div className="mb-6">
              <h2
                className="font-bold mb-2"
                style={{
                  fontFamily: "'Archivo Narrow', sans-serif",
                  fontSize: 'clamp(16px, 2.5vw, 22px)',
                  color: 'var(--m-ink)',
                  letterSpacing: '0.01em',
                  lineHeight: 1.2,
                }}
              >
                {step.question}
              </h2>
              <p
                style={{
                  fontSize: 13,
                  color: 'var(--m-ink-6)',
                  fontFamily: "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                }}
              >
                {step.subtext}
              </p>
              {step.multiple && (
                <p
                  className="font-mono text-[9px] uppercase tracking-widest mt-2"
                  style={{ color: 'var(--m-muted)' }}
                >
                  Select all that apply
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {step.options.map((option) => {
                const selected = isSelected(option.value);
                return (
                  <button
                    key={option.id}
                    onClick={() => handleSelect(option.value)}
                    className="text-left p-4 transition-all"
                    style={{
                      border: `1px solid ${selected ? 'var(--m-ink)' : 'rgba(var(--m-ink-rgb),0.1)'}`,
                      background: selected ? 'rgba(var(--m-ink-rgb),0.03)' : 'var(--m-bg)',
                      outline: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="flex items-center justify-center flex-shrink-0 mt-0.5 transition-all"
                        style={{
                          width: 14,
                          height: 14,
                          border: `1px solid ${selected ? 'var(--m-ink)' : 'rgba(var(--m-ink-rgb),0.2)'}`,
                          background: selected ? 'var(--m-ink)' : 'transparent',
                          borderRadius: step.multiple ? 2 : '50%',
                        }}
                      >
                        {selected && (
                          <div
                            style={{
                              width: 5,
                              height: 5,
                              background: 'var(--m-bg)',
                              borderRadius: step.multiple ? 1 : '50%',
                            }}
                          />
                        )}
                      </div>
                      <div>
                        <p
                          className="font-semibold mb-0.5"
                          style={{
                            fontSize: 13,
                            color: selected ? 'var(--m-ink)' : 'var(--m-ink-3)',
                            fontFamily:
                              "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                          }}
                        >
                          {option.label}
                        </p>
                        <p
                          style={{
                            fontSize: 11,
                            color: 'var(--m-muted)',
                            fontFamily:
                              "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                            lineHeight: 1.5,
                          }}
                        >
                          {option.desc}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div
          className="flex items-center justify-between mt-8 pt-6"
          style={{ borderTop: '1px solid rgba(var(--m-ink-rgb),0.08)' }}
        >
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className="tap-target inline-flex gap-1.5 font-mono text-[10px] uppercase tracking-widest transition-opacity pr-3 -ml-2 pl-2"
            style={{
              color: 'var(--m-muted)',
              background: 'transparent',
              border: 'none',
              cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
              opacity: currentStep === 0 ? 0.25 : 1,
            }}
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Back
          </button>

          <button
            onClick={handleNext}
            disabled={!canAdvance}
            /* Primary action: taller than the 39px it was, and given room to
               grow on a phone where it is the only thing to press. */
            className="inline-flex items-center justify-center gap-2 font-mono uppercase text-[10px] tracking-widest px-6 min-h-[48px] flex-1 sm:flex-none max-w-[16rem] sm:max-w-none ml-4 transition-opacity"
            style={{
              background: 'var(--m-ink)',
              color: 'var(--m-bg)',
              border: 'none',
              cursor: canAdvance ? 'pointer' : 'not-allowed',
              opacity: canAdvance ? 1 : 0.3,
            }}
          >
            {isLastStep ? 'View My Results' : 'Continue'}
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
